/**
 * Entity resolver — ARCHITECTURE.md section 3.5.
 *
 * Resolves entities across sources using a tiered matching strategy:
 *   Strong:   Identifier match (MMSI, IMO, ICAO24) → auto-merge
 *   Moderate: Name + type + context match → auto-merge with notification
 *   Weak:     Fuzzy name match → flag for analyst review, NEVER auto-merge
 *
 * Merge combines attributes, unions relationships + source references,
 * and keeps both provenance records.
 */

import type { NormalizedEvent } from '@/sources/base-source';
import type {
  Entity,
  EntityResolver,
  EntityMatch,
  EntityIdentifier,
  MatchSuggestion,
} from './types';
import { entityStore } from './entity-store';
import { getIdentifiers, isValidIdentifier, normalizeName } from './matching-utils';

// ---------------------------------------------------------------------------
// Match strength
// ---------------------------------------------------------------------------

type MatchStrength = 'strong' | 'moderate' | 'weak';

interface InternalMatch {
  entityId: string;
  entity: Entity;
  strength: MatchStrength;
  reason: string;
  confidence: number;
}

// ---------------------------------------------------------------------------
// EntityResolver implementation
// ---------------------------------------------------------------------------

class EntityResolverImpl implements EntityResolver {
  /**
   * Given a normalized event, find existing entities that may match.
   * Returns matches ranked by confidence.
   */
  async resolve(event: NormalizedEvent): Promise<EntityMatch[]> {
    if (!event.title) return [];

    // Search existing entities by text
    const candidates = await entityStore.search({
      text: event.title,
      limit: 20,
    });

    if (candidates.length === 0) return [];

    const matches: EntityMatch[] = [];
    for (const candidate of candidates) {
      // Simple text overlap scoring for event-to-entity matching
      const normalizedTitle = normalizeName(event.title);
      const normalizedName = normalizeName(candidate.name);

      let confidence = 0;
      if (normalizedTitle.includes(normalizedName) || normalizedName.includes(normalizedTitle)) {
        confidence = 0.7;
      } else {
        // Word overlap
        const titleWords = normalizedTitle.split(/\s+/).filter(w => w.length >= 3);
        const nameWords = new Set(normalizedName.split(/\s+/).filter(w => w.length >= 3));
        let shared = 0;
        for (const w of titleWords) {
          if (nameWords.has(w)) shared++;
        }
        if (shared >= 2) confidence = 0.5;
        else if (shared >= 1) confidence = 0.3;
      }

      if (confidence > 0) {
        matches.push({
          entityId: candidate.id,
          name: candidate.name,
          type: candidate.type,
          confidence,
        });
      }
    }

    // Sort by confidence descending
    matches.sort((a, b) => b.confidence - a.confidence);
    return matches;
  }

  /**
   * Merge two entities. The second entity is absorbed into the first.
   *
   * Merge rules:
   *   - Combine aliases (union, deduplicated)
   *   - Union source references
   *   - Keep both provenance records (primary stays as provenance, secondary in sources)
   *   - Take the higher confidence
   *   - Union tags and notes
   *   - Prefer the more specific description
   *   - Merge properties (shallow merge, existing values take precedence unless empty)
   *   - Corroboration updated to reflect the merge
   */
  async merge(entityAId: string, entityBId: string): Promise<Entity> {
    const entityA = await entityStore.getById(entityAId);
    const entityB = await entityStore.getById(entityBId);

    if (!entityA) throw new Error(`Entity ${entityAId} not found`);
    if (!entityB) throw new Error(`Entity ${entityBId} not found`);

    // Union aliases (include B's name and aliases)
    const allAliases = new Set(entityA.aliases);
    allAliases.add(entityB.name);
    for (const alias of entityB.aliases) {
      allAliases.add(alias);
    }
    // Remove the primary name from aliases
    allAliases.delete(entityA.name);

    // Union sources
    const mergedSources = [...entityA.sources];
    for (const src of entityB.sources) {
      if (!mergedSources.some(s => s.sourceId === src.sourceId && s.eventId === src.eventId)) {
        mergedSources.push(src);
      }
    }

    // Add B's provenance as a source reference
    if (entityB.provenance.sourceUrl) {
      mergedSources.push({
        sourceId: entityB.provenance.collectorId,
        eventId: entityB.id,
        timestamp: entityB.provenance.collectedAt,
        snippet: entityB.provenance.extractionQuote,
        confidence: entityB.provenance.confidence,
      });
    }

    // Merge properties (shallow)
    const mergedProperties: Record<string, unknown> = { ...entityA.properties };
    for (const [key, value] of Object.entries(entityB.properties)) {
      if (!(key in mergedProperties) || mergedProperties[key] === null || mergedProperties[key] === undefined) {
        mergedProperties[key] = value;
      } else if (key === 'identifiers' && Array.isArray(value)) {
        // Union identifiers
        const existing = Array.isArray(mergedProperties[key]) ? mergedProperties[key] as EntityIdentifier[] : [];
        const existingSet = new Set(existing.map(id => `${id.type}:${id.value}`));
        for (const id of value as EntityIdentifier[]) {
          const key2 = `${id.type}:${id.value}`;
          if (!existingSet.has(key2)) {
            existing.push(id);
          }
        }
        mergedProperties.identifiers = existing;
      }
    }

    // Union tags and notes
    const mergedTags = Array.from(new Set([...entityA.tags, ...entityB.tags]));
    const mergedNotes = [...entityA.notes, ...entityB.notes];

    // Merge corroboration
    const allCorrobSources = new Set([
      ...entityA.corroboration.sources,
      ...entityB.corroboration.sources,
    ]);

    // Update entity A with merged data
    const updated = await entityStore.update(entityAId, {
      aliases: Array.from(allAliases),
      description: entityA.description || entityB.description,
      properties: mergedProperties,
      sources: mergedSources,
      confidence: Math.max(entityA.confidence, entityB.confidence),
      tags: mergedTags,
      notes: mergedNotes,
      lastSeen: entityA.lastSeen > entityB.lastSeen ? entityA.lastSeen : entityB.lastSeen,
      firstSeen: entityA.firstSeen < entityB.firstSeen ? entityA.firstSeen : entityB.firstSeen,
      corroboration: {
        count: allCorrobSources.size,
        sources: Array.from(allCorrobSources),
        natoRating: entityA.corroboration.natoRating, // Will be recomputed by corroboration engine
      },
    });

    // Transfer B's relationships to A
    const bRels = await entityStore.getRelationships(entityBId);
    for (const rel of bRels) {
      const newSourceId = rel.sourceEntityId === entityBId ? entityAId : rel.sourceEntityId;
      const newTargetId = rel.targetEntityId === entityBId ? entityAId : rel.targetEntityId;

      // Don't create self-referential relationships
      if (newSourceId === newTargetId) continue;

      await entityStore.addRelationship({
        sourceEntityId: newSourceId,
        targetEntityId: newTargetId,
        type: rel.type,
        confidence: rel.confidence,
        evidence: rel.evidence,
        firstSeen: rel.firstSeen,
        lastSeen: rel.lastSeen,
      });
    }

    // Delete entity B
    await entityStore.delete(entityBId);

    return updated;
  }

  /**
   * Suggest potential entity matches across the store.
   *
   * Match tiers (ARCHITECTURE.md section 3.5):
   *   Strong: identifier match → auto-merge safe
   *   Moderate: name + type + context → auto-merge with notification
   *   Weak: fuzzy name → requires analyst review, NEVER auto-merge
   */
  async suggestMatches(): Promise<MatchSuggestion[]> {
    const suggestions: MatchSuggestion[] = [];

    // Get all entities
    const allEntities = await entityStore.search({ limit: 500 });
    if (allEntities.length < 2) return [];

    // Compare pairs
    const seen = new Set<string>();

    for (let i = 0; i < allEntities.length; i++) {
      for (let j = i + 1; j < allEntities.length; j++) {
        const a = allEntities[i];
        const b = allEntities[j];

        const pairKey = [a.id, b.id].sort().join(':');
        if (seen.has(pairKey)) continue;
        seen.add(pairKey);

        const match = this.compareEntities(a, b);
        if (match) {
          suggestions.push({
            entityA: a,
            entityB: b,
            confidence: match.confidence,
            reason: match.reason,
          });
        }
      }
    }

    // Sort by confidence descending
    suggestions.sort((a, b) => b.confidence - a.confidence);
    return suggestions;
  }

  /**
   * Compare two entities for potential match.
   * Returns null if no match detected.
   */
  private compareEntities(a: Entity, b: Entity): InternalMatch | null {
    // Strong: identifier match across entities
    const idsA = getIdentifiers(a);
    const idsB = getIdentifiers(b);

    for (const idA of idsA) {
      for (const idB of idsB) {
        if (idA.type === idB.type && idA.value === idB.value && isValidIdentifier(idA)) {
          return {
            entityId: b.id,
            entity: b,
            strength: 'strong',
            reason: `Identifier match: ${idA.type} ${idA.value}`,
            confidence: 0.95,
          };
        }
      }
    }

    // Same type required for name matching
    if (a.type !== b.type) return null;

    // Moderate: exact name or alias match
    const nameA = normalizeName(a.name);
    const nameB = normalizeName(b.name);

    if (nameA === nameB) {
      return {
        entityId: b.id,
        entity: b,
        strength: 'moderate',
        reason: `Exact name match: "${a.name}"`,
        confidence: 0.85,
      };
    }

    // Check aliases
    const aliasesA = a.aliases.map(normalizeName);
    const aliasesB = b.aliases.map(normalizeName);

    if (aliasesA.includes(nameB) || aliasesB.includes(nameA)) {
      return {
        entityId: b.id,
        entity: b,
        strength: 'moderate',
        reason: `Name/alias match: "${a.name}" ↔ "${b.name}"`,
        confidence: 0.8,
      };
    }

    for (const aliasA of aliasesA) {
      if (aliasesB.includes(aliasA)) {
        return {
          entityId: b.id,
          entity: b,
          strength: 'moderate',
          reason: `Alias match: "${aliasA}"`,
          confidence: 0.75,
        };
      }
    }

    // Weak: fuzzy match — NEVER auto-merge
    if (nameA.length >= 3 && nameB.length >= 3) {
      if (nameA.includes(nameB) || nameB.includes(nameA)) {
        return {
          entityId: b.id,
          entity: b,
          strength: 'weak',
          reason: `Fuzzy match: "${a.name}" contains/contained in "${b.name}"`,
          confidence: 0.5,
        };
      }

      // Word overlap
      const wordsA = nameA.split(/\s+/).filter(w => w.length >= 3);
      const wordsB = new Set(nameB.split(/\s+/).filter(w => w.length >= 3));
      let shared = 0;
      for (const w of wordsA) {
        if (wordsB.has(w)) shared++;
      }
      if (shared >= 2) {
        return {
          entityId: b.id,
          entity: b,
          strength: 'weak',
          reason: `Fuzzy match: ${shared} shared words between "${a.name}" and "${b.name}"`,
          confidence: 0.4,
        };
      }
    }

    return null;
  }
}

export const entityResolver: EntityResolver = new EntityResolverImpl();
